/**
 * biometric.ts — fingerprint / Face unlock for the native app.
 *
 * After the user logs in once (native), they can enable biometric unlock: the
 * session token is stored in the OS secure store (Android Keystore) and, on the
 * next app open, a fingerprint prompt unlocks the app instead of typing the
 * password. All functions are no-ops / false on the web.
 */
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';
import { setAuthToken } from './api';

const SERVER = 'reformu.lat';
const FLAG = 'reformulat_biometric_enabled';

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/** Whether the user has opted into biometric unlock (and we're on native). */
export function isBiometricEnabled(): boolean {
  try {
    return isNative() && localStorage.getItem(FLAG) === '1';
  } catch {
    return false;
  }
}

/** Whether the device actually has biometrics enrolled/available. */
export async function biometricAvailable(): Promise<boolean> {
  if (!isNative()) return false;
  try {
    const r = await NativeBiometric.isAvailable();
    return !!r.isAvailable;
  } catch {
    return false;
  }
}

/** Enable biometric unlock: securely store the token + set the opt-in flag. */
export async function enableBiometric(username: string, token: string): Promise<boolean> {
  if (!isNative() || !token) return false;
  try {
    await NativeBiometric.setCredentials({ username, password: token, server: SERVER });
    localStorage.setItem(FLAG, '1');
    return true;
  } catch {
    return false;
  }
}

/** Disable biometric unlock and wipe the stored credentials. */
export async function disableBiometric(): Promise<void> {
  try {
    localStorage.removeItem(FLAG);
  } catch {
    /* ignore */
  }
  if (!isNative()) return;
  try {
    await NativeBiometric.deleteCredentials({ server: SERVER });
  } catch {
    /* ignore */
  }
}

/**
 * Prompt the fingerprint/Face dialog and, on success, return the stored token
 * (also re-applying it as the active auth token). Returns null on cancel/failure.
 */
export async function unlockWithBiometric(): Promise<string | null> {
  if (!isNative()) return null;
  try {
    await NativeBiometric.verifyIdentity({
      reason: 'Desbloqueie o reformu.lat',
      title: 'reformu.lat',
      subtitle: 'Use sua digital para entrar',
    });
    const creds = await NativeBiometric.getCredentials({ server: SERVER });
    const token = creds?.password ?? null;
    if (token) setAuthToken(token);
    return token;
  } catch {
    return null;
  }
}
